import React, { useCallback, useEffect, useState } from 'react';
import './FMSDashboard.css'; 
import './ReturnReverseLogisticsFMS.css'; 
import { getInitials, getUserColor, getLocalDateKey, FmsDateRangePicker } from './FMSDashboard';
import AppSidebar from './AppSidebar';

// EXACT MATCH TO DB COLUMNS
const REVERSE_WORKFLOW_STEPS = [
  { key: 'reverse_pickup_generated', label: 'Reverse Pickup (UC)', backendKey: 'reverse_pickup_uc' },
  { key: 'reverse_pickup_done', label: 'Pickup Done', backendKey: 'reverse_pickup_do' },
  { key: 'replacement_order_created', label: 'Replacement (UC)', backendKey: 'replacement_created_uc' },
  { key: 'replacement_dispatched', label: 'Replacement Dispatched', backendKey: 'replacement_dispatched' },
  { key: 'refund_processed', label: 'Refund Processed', backendKey: 'refund_processed' },
  { key: 'credit_note_generated', label: 'Credit Note (UC)', backendKey: 'credit_note_generate_uc' },
];

function FmsIcon({ type, size = 18 }) {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' };
  switch (type) {
    case 'box': return <svg {...common} stroke="currentColor" strokeWidth="1.8"><path d="m12 3 8 4-8 4-8-4 8-4Z" /><path d="m4 7 8 4 8-4v10l-8 4-8-4V7Zm8 4v10" /></svg>;
    case 'clock': return <svg {...common} stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="8"/><path d="M12 8v4l3 2"/></svg>;
    case 'check': return <svg {...common} stroke="currentColor" strokeWidth="2.3"><path d="m5 12 4 4L19 6"/></svg>;
    case 'alert':
  return (
    <svg {...common} stroke="currentColor" strokeWidth="1.8">
      <path d="M12 4 3 20h18L12 4Z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 9v4" strokeLinecap="round" />
      <circle cx="12" cy="16" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
    default: return null;
  }
}

export default function ReturnReverseLogisticsFMS({
  user,
  onLogout,
  loggingOut,
  onNavigateToDashboard,
  onNavigateToTasks,
  onNavigateToFMS,
  onNavigateToReturns,
  onNavigateToDelegation,
  onNavigateToAllTasks,
  onNavigateToResourceControl,
  onNavigateToLogging
}) {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [editingCell, setEditingCell] = useState(null); 
  const [loadingCells, setLoadingCells] = useState(new Set()); 
const hasAccess = user?.role === 1 || user?.role === 2;
  // NEW: State for the dropdown filters
  const [requestTypeFilter, setRequestTypeFilter] = useState('All');
  const [dateFilter, setDateFilter] = useState('All');

  // NEW: State for the custom date range
  const [customStartDate, setCustomStartDate] = useState(() => getLocalDateKey(new Date()));
  const [customEndDate, setCustomEndDate] = useState(() => getLocalDateKey(new Date()));
 useEffect(() => {
      document.title = 'Return/Reverse Processing FMS ';
    }, []);
// Add after your state declarations
const hasReturnReverseAccess = () => {
  if (!user || !user.resource_access) {
    return false;
  }
  return user.resource_access.Return_reverse_logistics === true;
};

const accessGranted = hasReturnReverseAccess();
  // // NEW: State for the dropdown filters
  // const [requestTypeFilter, setRequestTypeFilter] = useState('All');
  // const [dateFilter, setDateFilter] = useState('All');

  // NEW: Extract unique request types dynamically for the dropdown
  const uniqueRequestTypes = ['All', ...new Set(orders.map(o => o.request_type).filter(Boolean))];

  // UPDATED: Filtering logic including Search, Date, and Request Type
  const filteredOrders = orders.filter(order => {
    // 1. Text Search Filter
    const searchString = `${order.order_code || ''} ${order.order_id || ''} ${order.product_name || order.name || ''} ${order.customer_name || ''}`.toLowerCase();
    const matchesSearch = searchString.includes(searchTerm.toLowerCase());

    // 2. Request Type Filter
    const matchesRequestType = requestTypeFilter === 'All' || order.request_type === requestTypeFilter;

    // 3. Date Filter (Based on the first step's date)
    let matchesDate = true;
    if (dateFilter !== 'All') {
      const firstStep = order.workflow_steps?.[0];
      const orderDateStr = firstStep?.actual_date || firstStep?.planned_date;
      
      if (orderDateStr && orderDateStr !== 'Pending') {
        const orderDate = new Date(orderDateStr.replace(' ', 'T'));
        
        if (dateFilter === 'Custom Range') {
          // Check if date falls within custom bounds
          const orderDateKey = getLocalDateKey(orderDate);
          matchesDate = orderDateKey >= customStartDate && orderDateKey <= customEndDate;
        } else {
          // Standard day difference logic
          const now = new Date();
          const diffTime = Math.abs(now - orderDate);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          if (dateFilter === 'Today') matchesDate = diffDays <= 1;
          else if (dateFilter === 'Last 7 Days') matchesDate = diffDays <= 7;
          else if (dateFilter === 'Last 30 Days') matchesDate = diffDays <= 30;
        }
      } else {
        matchesDate = false; // Exclude if no valid date and a specific date filter is applied
      }
    }

    return matchesSearch && matchesRequestType && matchesDate;
  });

  const fetchReturnOrders = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/reverse-return/returns'); 
      const data = await response.json();
      
      if (data.success || Array.isArray(data)) {
        setOrders(Array.isArray(data) ? data : data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch return orders:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReturnOrders();
  }, [fetchReturnOrders]);

  const handleSyncData = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/reverse-return/sync-reverse-return', { method: 'POST' });
      const result = await response.json();
      if (result.success) {
        await fetchReturnOrders();
      }
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCellUpdate = async (order, step, newValue) => {
    const targetOrderCode = order.order_id || order.order_code;
    const cellId = `${targetOrderCode}-${step.backendKey}`;
    
    setEditingCell(null);

    const isMarkingDone = newValue === 'Yes';
    const endpoint = isMarkingDone ? '/api/reverse-return/update-reverse-step' : '/api/reverse-return/undo-reverse-step';

    setLoadingCells(prev => new Set(prev).add(cellId));

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderCode: targetOrderCode, stepName: step.backendKey })
      });

      if (response.ok) {
        await fetchReturnOrders(); 
      } else {
        console.error('Failed to update cell');
      }
    } catch (error) {
      console.error('Error updating cell:', error);
    } finally {
      setLoadingCells(prev => {
        const next = new Set(prev);
        next.delete(cellId);
        return next;
      });
    }
  };

  // const filteredOrders = orders.filter(order => {
  //   const searchString = `${order.order_code || ''} ${order.order_id || ''} ${order.product_name || order.name || ''} ${order.customer_name || ''}`.toLowerCase();
  //   return searchString.includes(searchTerm.toLowerCase());
  // });

//   const pendingCount = orders.filter((o) => {
//   const status = String(o.overall_status || '').trim().toLowerCase();
//   return !status || ['pending', 'not started', 'new', 'awaiting'].includes(status);
// }).length;


// Calculate KPI metrics based on workflow step statuses
  let completedCount = 0;
  let inProgressCount = 0;
  let pendingCount = 0;
  
  // NEW: Initialize an object to store counts for each individual workflow step
  const stepCompletionCounts = {};
  REVERSE_WORKFLOW_STEPS.forEach(step => {
    stepCompletionCounts[step.backendKey] = 0;
  });

  filteredOrders.forEach(order => {
    let isAllCompleted = true;
    let hasAnyCompleted = false;
    let validStepsExist = false;

    REVERSE_WORKFLOW_STEPS.forEach(step => {
      const stepData = (order.workflow_steps || []).find(s => s.backendKey === step.backendKey);
      const status = stepData?.status;
      const isNA = status === 'NA';
      const isDone = status === 'Yes' || status === 'completed' || stepData?.done;

      if (!isNA) {
        validStepsExist = true;
        if (isDone) {
          hasAnyCompleted = true;
          // NEW: Increment the specific step counter if it's done
          stepCompletionCounts[step.backendKey]++;
        } else {
          isAllCompleted = false;
        }
      }
    });

    if (!validStepsExist || isAllCompleted) {
      completedCount++;
    } else if (hasAnyCompleted) {
      inProgressCount++;
    } else {
      pendingCount++;
    }
  });

  return (
    <div className="fms-shell rrl-shell">
      <AppSidebar
        user={user}
        active="returns"
        onLogout={onLogout}
        loggingOut={loggingOut}
        onNavigateToDashboard={onNavigateToDashboard}
        onNavigateToTasks={onNavigateToTasks}
        onNavigateToFMS={onNavigateToFMS}
        onNavigateToReturns={onNavigateToReturns}
        onNavigateToDelegation={onNavigateToDelegation}
        onNavigateToAllTasks={onNavigateToAllTasks}
        onNavigateToResourceControl={onNavigateToResourceControl}
        onNavigateToLogging={onNavigateToLogging}
      />
 {accessGranted ? (
      <main className="fms-main rrl-main">
        <header className="fms-topbar rrl-topbar">
          <div className="mobile-left">
            <button className="mobile-menu-btn" type="button" aria-label="Open menu" />
          </div>

          {/* <div className="fms-global-search">
            <input 
              type="search" 
              placeholder="Search orders, customers, or products..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div> */}

           <div className="topbar-actions">
            {/* <button className="icon-btn icon-view desktop-view-trigger" type="button" aria-label="View options" />
            <button className="icon-btn icon-view mobile-nav-trigger" type="button" aria-label="Open navigation" /> */}
            <div className="avatar-placeholder" style={{ background: getUserColor(user?.emp_name || user?.username) }}>
              {getInitials(user)}
            </div>
          </div>
        </header>

        <div className="rrl-content">
          <div className="rrl-page">
            
            <section className="rrl-hero">
  <div>
    <h2>Return & Reverse Management</h2>
    <p>Double-click the blue cells to update workflow statuses.</p>
  </div>
  
<div className="rrl-hero-actions">
    {/* UPDATED: Date Filter Dropdown */}
    <select 
      className="rrl-filter-dropdown" 
      value={dateFilter} 
      onChange={(e) => setDateFilter(e.target.value)}
    >
      <option value="All">All Dates</option>
      <option value="Today">Today</option>
      <option value="Last 7 Days">Last 7 Days</option>
      <option value="Last 30 Days">Last 30 Days</option>
      <option value="Custom Range">Custom Range</option>
    </select>

    {/* NEW: Render Custom Date Range Picker when selected */}
    {dateFilter === 'Custom Range' && (
      <div className="fms-date-range-control" style={{ minWidth: '190px' }}>
        <FmsDateRangePicker
          startDate={customStartDate}
          endDate={customEndDate}
          onDateChange={(start, end) => {
            setCustomStartDate(start);
            setCustomEndDate(end);
          }}
        />
      </div>
    )}

    {/* NEW: Request Type Dropdown */}
    <select 
      className="rrl-filter-dropdown" 
      value={requestTypeFilter} 
      onChange={(e) => setRequestTypeFilter(e.target.value)}
    >
      {uniqueRequestTypes.map(type => (
        <option key={type} value={type}>
          {type === 'All' ? 'All Request Types' : type}
        </option>
      ))}
    </select>

    <button onClick={handleSyncData} className="rrl-icon-button">
      {isLoading ? 'Syncing...' : 'Sync Excel Data'}
    </button>
  </div>
</section>

            <section className="rrl-summary-grid rrl-summary-grid-compact">
              {/* Existing Main KPIs */}
              <article className="rrl-stat-card rrl-card-indigo">
                <div className="rrl-stat-icon">
                  <FmsIcon type="box" size={18} />
                </div>
                <div className="rrl-stat-copy">
                  <p>Total Returns</p>
                  <strong>{isLoading ? '-' : filteredOrders.length}</strong>
                </div>
              </article>

              <article className="rrl-stat-card rrl-card-blue">
                <div className="rrl-stat-icon">
                  <FmsIcon type="clock" size={18} />
                </div>
                <div className="rrl-stat-copy">
                  <p>In Progress</p>
                  <strong>{isLoading ? '-' : inProgressCount}</strong>
                </div>
              </article>

              <article className="rrl-stat-card rrl-card-amber">
                <div className="rrl-stat-icon">
                  <FmsIcon type="alert" size={18} />
                </div>
                <div className="rrl-stat-copy">
                  <p>Pending</p>
                  <strong>{isLoading ? '-' : pendingCount}</strong>
                </div>
              </article>

              <article className="rrl-stat-card rrl-card-emerald">
                <div className="rrl-stat-icon">
                  <FmsIcon type="check" size={18} />
                </div>
                <div className="rrl-stat-copy">
                  <p>Completed</p>
                  <strong>{isLoading ? '-' : completedCount}</strong>
                </div>
              </article>

              {/* NEW: Dynamically generated KPIs for each workflow step */}
              {REVERSE_WORKFLOW_STEPS.map(step => (
                <article key={`kpi-${step.backendKey}`} className="rrl-stat-card rrl-card-blue">
                  <div className="rrl-stat-icon">
                    <FmsIcon type="check" size={18} />
                  </div>
                  <div className="rrl-stat-copy">
                    <p>{step.label}</p>
                    <strong>{isLoading ? '-' : stepCompletionCounts[step.backendKey]}</strong>
                  </div>
                </article>
              ))}
            </section>

            <div className="rrl-table-container">
              <table className="rrl-data-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer Name</th>
                    <th>Product</th>
                    <th>Request Type</th>
                    {REVERSE_WORKFLOW_STEPS.map(step => (
                      <th key={step.key} className="workflow-col" title={step.backendKey}>
                        {step.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={4 + REVERSE_WORKFLOW_STEPS.length} style={{ textAlign: 'center', padding: '40px' }}>
                        Loading data...
                      </td>
                    </tr>
                  ) : filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan={4 + REVERSE_WORKFLOW_STEPS.length} style={{ textAlign: 'center', padding: '40px' }}>
                        No orders found.
                      </td>
                    </tr>
                  ) : (
                    filteredOrders.map(order => {
                      const orderKey = order.order_id || order.order_code;
                      
                      // NEW LOGIC: Check if all steps are either 'Yes', 'completed', 'done', or 'NA'
                      const isAllCompleted = REVERSE_WORKFLOW_STEPS.every(step => {
                        const stepData = (order.workflow_steps || []).find(s => s.backendKey === step.backendKey);
                        return (
                          stepData?.status === 'Yes' ||
                          stepData?.status === 'completed' ||
                          stepData?.done ||
                          stepData?.status === 'NA'
                        );
                      });
                      
                      return (
                        <tr key={orderKey} className={isAllCompleted ? 'rrl-row-completed' : ''}>
                          <td><strong>{orderKey}</strong></td>
                          <td>{order.customer_name || 'N/A'}</td>
                          <td><span title={order.name || order.product_name} style={{ maxWidth: '200px', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis' }}>{order.name || order.product_name || 'N/A'}</span></td>
                          <td>{order.request_type || 'N/A'}</td>
                          
                        {/* Render Editable Workflow Cells */}
                          {REVERSE_WORKFLOW_STEPS.map(step => {
                            const stepData = (order.workflow_steps || []).find(s => s.backendKey === step.backendKey);
                            
                            // Check Status Type
                            const isNA = stepData?.status === 'NA';
                            const isDone = stepData?.status === 'Yes' || stepData?.status === 'completed' || stepData?.done;
                            
                            const cellId = `${orderKey}-${step.backendKey}`;
                            const isEditing = editingCell?.orderCode === orderKey && editingCell?.stepKey === step.backendKey;
                            const isSaving = loadingCells.has(cellId);

                            return (
                              <td 
                                key={step.key} 
                                className={`workflow-cell ${isNA ? 'is-disabled' : ''}`}
                                onDoubleClick={() => {
                                  if (!isSaving && !isNA) {
                                    setEditingCell({ orderCode: orderKey, stepKey: step.backendKey });
                                  }
                                }}
                              >
                                {isSaving ? (
                                  <span className="rrl-status-badge status-loading">Saving...</span>
                                ) : isEditing ? (
                                  
                                  /* --- CUSTOM DROPDOWN UI --- */
                                  <div 
                                    className="rrl-custom-dropdown-container"
                                    tabIndex={0}
                                    onBlur={(e) => {
                                      // Close if clicked outside
                                      if (!e.currentTarget.contains(e.relatedTarget)) {
                                        setEditingCell(null);
                                      }
                                    }}
                                    ref={el => el && el.focus()}
                                  >
                                    <div className="rrl-dropdown-menu">
  <button 
    className="rrl-dropdown-item yes-option" 
    onMouseDown={() => handleCellUpdate(order, step, 'Yes')}
  >
    <span style={{ visibility: isDone ? 'visible' : 'hidden', display: 'flex' }}>
      <FmsIcon type="check" size={14} />
    </span> 
    Yes
  </button>
  <button 
    className="rrl-dropdown-item no-option" 
    onMouseDown={() => handleCellUpdate(order, step, 'No')}
  >
    <span style={{ visibility: !isDone ? 'visible' : 'hidden', display: 'flex' }}>
      <FmsIcon type="check" size={14} />
    </span> 
    No
  </button>
</div>
                                  </div>

                                ) : isNA ? (
                                  <span className="rrl-status-badge status-na">N/A</span>
                                ) : (
                                  <span className={`rrl-status-badge ${isDone ? 'status-yes' : 'status-no'}`}>
                                    {isDone ? 'Yes' : 'No'}
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </div>
      </main>
    ) : (
      <main className="fms-main rrl-main" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh'
      }}>
        <div style={{
          background: '#fef3c7',
          border: '1px solid #fcd34d',
          borderRadius: '8px',
          padding: '32px 40px',
          maxWidth: '500px',
          textAlign: 'center'
        }}>
          <h2 style={{
            margin: '0 0 12px 0',
            fontSize: '20px',
            fontWeight: '700',
            color: '#92400e'
          }}>
            🔒 Access Denied
          </h2>
          <p style={{
            margin: '0 0 20px 0',
            fontSize: '14px',
            color: '#b45309',
            lineHeight: '1.6'
          }}>
            You don't have access to Return & Reverse Logistics FMS. Please contact your administrator to request access.
          </p>
          <button
            type="button"
            onClick={onNavigateToDashboard}
            style={{
              background: '#1f5f55',
              color: 'white',
              border: 'none',
              padding: '10px 24px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </main>
    )}
  </div>
);
}
